import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';
import { cn } from './cn.js';
import { currencySymbol, formatCents, parseCentsFromDigits } from './money.js';

export interface CurrencyInputProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'value' | 'onChange' | 'type'
> {
  label: string;
  currency?: string | undefined;
  value: number | null;
  onValueChange: (cents: number | null) => void;
  error?: string | undefined;
  hint?: string | undefined;
  /// Rótulo só para leitor de tela, quando a linha ao lado já diz o que é o campo.
  hideLabel?: boolean | undefined;
  /// Ao lado do rótulo, como a ajuda contextual do campo.
  labelAddon?: ReactNode | undefined;
}

/// Campo de valor na moeda informada. Digita-se da direita para a esquerda, como em caixa
/// eletrônico: os dois últimos dígitos são sempre os centavos. O componente fala em centavos
/// inteiros para fora, então nenhum valor passa por ponto flutuante no caminho até a API.
export const CurrencyInput = forwardRef<HTMLInputElement, CurrencyInputProps>(
  (
    {
      className,
      currency = 'BRL',
      error,
      hideLabel = false,
      hint,
      id: providedId,
      label,
      labelAddon,
      value,
      onValueChange,
      onFocus,
      onClick,
      onKeyDown,
      ...props
    },
    ref,
  ) => {
    const generatedId = useId();
    const id = providedId ?? generatedId;
    const descriptionId = error ? `${id}-error` : hint ? `${id}-hint` : undefined;
    return (
      <div className={cn('min-w-0', !hideLabel && 'space-y-2')}>
        {labelAddon && !hideLabel ? (
          <span className="flex items-center gap-1">
            <label htmlFor={id} className="block text-xs font-semibold text-content/70">
              {label}
            </label>
            {labelAddon}
          </span>
        ) : (
          <label
            htmlFor={id}
            className={cn('block text-xs font-semibold text-content/70', hideLabel && 'sr-only')}
          >
            {label}
          </label>
        )}
        <div className="relative min-w-0">
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 left-0 grid w-11 place-items-center text-xs font-semibold text-content-faint"
          >
            {currencySymbol(currency)}
          </span>
          <input
            ref={ref}
            id={id}
            inputMode="numeric"
            autoComplete="off"
            value={value === null ? '' : formatCents(value)}
            placeholder="0,00"
            onChange={(event) => onValueChange(parseCentsFromDigits(event.target.value))}
            aria-invalid={Boolean(error)}
            aria-describedby={descriptionId}
            className={cn(
              'hue-input h-12 w-full min-w-0 rounded-xl border bg-surface-elevated pl-11 pr-4 text-right text-sm tabular-nums text-content outline-none transition placeholder:text-content-faint focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20',
              error ? 'border-danger/70' : 'border-surface-border',
              className,
            )}
            {...props}
            onFocus={(event) => {
              onFocus?.(event);
              const field = event.currentTarget;
              queueMicrotask(() => {
                if (field.selectionStart === field.selectionEnd) {
                  const end = field.value.length;
                  field.setSelectionRange(end, end);
                }
              });
            }}
            onKeyDown={(event) => {
              onKeyDown?.(event);
              const field = event.currentTarget;
              if (
                !event.defaultPrevented &&
                !event.ctrlKey &&
                !event.metaKey &&
                /^[0-9]$/.test(event.key) &&
                field.selectionStart === field.selectionEnd
              ) {
                const end = field.value.length;
                field.setSelectionRange(end, end);
              }
            }}
            onClick={(event) => {
              onClick?.(event);
              if (event.currentTarget.selectionStart === event.currentTarget.selectionEnd) {
                const end = event.currentTarget.value.length;
                event.currentTarget.setSelectionRange(end, end);
              }
            }}
          />
        </div>
        {error ? (
          <p id={descriptionId} role="alert" className="text-xs text-danger">
            {error}
          </p>
        ) : hint ? (
          <p id={descriptionId} className="text-xs leading-5 text-content-muted">
            {hint}
          </p>
        ) : null}
      </div>
    );
  },
);
CurrencyInput.displayName = 'CurrencyInput';
